import React, { useState, useEffect } from 'react';
import { Calendar, Star, Play, ChevronRight } from 'lucide-react';
import EventParticipants from "./EventParticipants";
import { API_CONFIG } from '../config/apiConfig';

const UpcomingEvents = ({ onOpenTheater }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const getAdminToken = () => {
        return localStorage.getItem('cinestream_token')
    };

    useEffect(() => {
        fetchUpcomingEvents();
        const interval = setInterval(fetchUpcomingEvents, 60000);
        return () => clearInterval(interval);
    }, []);

    const fetchUpcomingEvents = async () => {
        try {
            const token = getAdminToken();
            // ✅ CHANGEMENT : Laravel API
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watch-party/events?filter=upcoming`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token || ''
                }
            });
            const data = await response.json();
            if (data.success) {
                // ✅ Trier par date (le plus proche en premier)
                const sortedEvents = (data.events || []).sort((a, b) => {
                    const dateA = new Date(a.scheduledDateTime);
                    const dateB = new Date(b.scheduledDateTime);
                    return dateA - dateB; // Ordre chronologique
                });
                setEvents(sortedEvents);
            }
        } catch (error) {
            console.error('Erreur chargement événements:', error);
        }
        setLoading(false);
    };

    // ✅ MISE À JOUR : Checker le status en priorité
    const getCountdown = (event) => {
        // Si l'événement est lancé → "En cours" en vert
        if (event.status === 'live') {
            return { text: '🔴 En cours', color: 'text-green-400' };
        }

        const now = new Date();
        const eventDate = new Date(event.scheduledDateTime);
        const diff = eventDate - now;

        if (diff < 0) {
            return { text: 'En cours', color: 'text-green-400' };
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
            return { text: `Dans ${days}j ${hours}h`, color: 'text-blue-400' };
        } else if (hours > 0) {
            return { text: `Dans ${hours}h ${minutes}min`, color: 'text-orange-400' };
        } else {
            return { text: `Dans ${minutes} min`, color: 'text-red-400' };
        }
    };

    const formatDate = (dateTimeStr) => {
        const date = new Date(dateTimeStr);
        return new Intl.DateTimeFormat('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    if (loading || events.length === 0) return null;

    const countdownColor = (c) => {
        if (c.text.includes('cours')) return '#22C55E';
        if (c.text.startsWith('Dans') && c.color === 'text-red-400') return '#EF4444';
        if (c.color === 'text-orange-400') return '#F97316';
        return '#60A5FA';
    };

    return (
        <div style={{ marginBottom: 32, maxWidth: 'var(--max-w)', margin: '0 auto 32px', padding: '0 20px' }}>
            <div style={{
                background: 'linear-gradient(135deg, rgba(232,183,90,0.12), rgba(156,116,47,0.08))',
                backdropFilter: 'blur(16px)',
                borderRadius: 'var(--r-2xl)',
                padding: '24px',
                border: '1px solid rgba(232,183,90,0.25)',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 42, height: 42, borderRadius: 'var(--r-lg)',
                            background: 'linear-gradient(135deg, var(--accent), var(--accent-deep))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 12px var(--accent-glow)',
                        }}>
                            <Calendar style={{ width: 20, height: 20, color: '#fff' }} />
                        </div>
                        <div>
                            <h2 style={{ color: 'var(--text-1)', fontWeight: 800, fontSize: '1.25rem' }}>🎬 Prochaines Watch Party</h2>
                            <p style={{ color: 'var(--accent-bright)', fontSize: '0.8125rem', marginTop: 2 }}>Séances de visionnage synchronisé</p>
                        </div>
                    </div>
                    <button
                        onClick={onOpenTheater}
                        className="btn-primary"
                        style={{ padding: '10px 20px', fontSize: '0.9375rem', gap: 6 }}
                    >
                        <span>Voir la salle</span>
                        <ChevronRight style={{ width: 16, height: 16 }} />
                    </button>
                </div>

                {/* Event cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: 16 }}>
                    {events.slice(0, 3).map(event => {
                        const countdown = getCountdown(event);
                        const isLive = event.status === 'live';

                        return (
                            <button
                                key={event.id}
                                onClick={onOpenTheater}
                                className="movie-card"
                                style={{
                                    background: 'var(--glass)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: 'var(--r-xl)',
                                    overflow: 'hidden',
                                    textAlign: 'left',
                                    width: '100%',
                                    cursor: 'pointer',
                                    transition: 'border-color 200ms ease, transform 200ms ease',
                                }}
                            >
                                {/* Image */}
                                <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                                    <img
                                        src={event.movieBackdrop || event.moviePoster}
                                        alt={event.movieTitle}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 300ms ease' }}
                                    />
                                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)' }} />

                                    {/* Countdown badge */}
                                    <div style={{
                                        position: 'absolute', top: 10, right: 10,
                                        padding: '3px 10px', borderRadius: 'var(--r-full)',
                                        background: isLive ? 'rgba(34,197,94,0.9)' : 'rgba(0,0,0,0.75)',
                                        backdropFilter: 'blur(8px)',
                                        animation: isLive ? 'glowPulse 2s infinite' : 'none',
                                    }}>
                                        <span style={{ color: isLive ? '#fff' : countdownColor(countdown), fontSize: '0.75rem', fontWeight: 700 }}>
                                            {countdown.text}
                                        </span>
                                    </div>

                                    {/* Play overlay */}
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        opacity: 0, transition: 'opacity 200ms ease',
                                    }} className="group-hover-show">
                                        <div className="play-btn" style={{ width: 56, height: 56 }}>
                                            <Play style={{ width: 22, height: 22, fill: '#fff', color: '#fff' }} />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ padding: '14px 16px' }}>
                                    <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1rem', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {event.movieTitle}
                                    </h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-4)', fontSize: '0.8125rem', marginBottom: 6 }}>
                                        <Calendar style={{ width: 13, height: 13 }} />
                                        <span>{formatDate(event.scheduledDateTime)}</span>
                                    </div>
                                    {event.movieRating && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: '#FCD34D', marginBottom: 6 }}>
                                            <Star style={{ width: 13, height: 13, fill: 'currentColor' }} />
                                            <span>{event.movieRating}/10</span>
                                        </div>
                                    )}
                                    <EventParticipants eventId={event.id} />
                                </div>
                            </button>
                        );
                    })}
                </div>

                {events.length > 3 && (
                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                        <button
                            onClick={onOpenTheater}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                color: 'var(--accent-bright)', background: 'none', border: 'none',
                                cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
                                transition: 'color 150ms ease',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--accent-bright)'}
                        >
                            <span>Voir tous les événements ({events.length})</span>
                            <ChevronRight style={{ width: 14, height: 14 }} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpcomingEvents;